import { useEffect, useState, useCallback } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { createSortable, destroySortable } from '../../utils/sorting';
import { isHiddenInOverview } from '../../utils/parts';
import {
  canAttachNotesTo,
  countAttachedToParts,
  getAttachedNotes,
  isAttachedToPart,
  isNotePart,
} from '../../utils/notes';
import { computePartNumbers } from '../../utils/partNumbers';
import NoContent from '../common/NoContent';
import SongPart from '../SongPart';

import './SongParts.scss';

function SongParts({
  editPartId,
  parts,
  overview,
  onPartMove,
  onPartDelete,
  onPartDuplicate,
  onPartEdit,
  onPartIntensity,
  onPartSort,
  onPartUpdate,
  flashingPartId,
  flashingType,
  refreshKey,
  onToggleAttach,
  timeSignature,
}) {
  const { t } = useTranslation();
  const [songPartsRef, setSongPartsRef] = useState(null);
  const [highlightedGroup, setHighlightedGroup] = useState(null);
  const [selectedPartId, setSelectedPartId] = useState(null);

  // A part's group is keyed by the original part's id (its own id, or its duplicateId if it is itself a duplicate)
  // Parts with no duplicate relationship are their own group of one
  const groupKeyFor = (part) => (part.isDuplicate ? part.duplicateId : part.id);

  const handleBackgroundClick = (part) => {
    const groupKey = groupKeyFor(part);
    setHighlightedGroup((prev) => (prev === groupKey ? null : groupKey));
  };

  // Opening a part's options menu highlights only that part and supersedes any background-click highlight
  const handleMenuOpenChange = (part, open) => {
    if (open) setHighlightedGroup(null);
    setSelectedPartId((prev) => (open ? part.id : prev === part.id ? null : prev));
  };

  const isPartHighlighted = (part) => {
    if (selectedPartId !== null) return part.id === selectedPartId;
    return highlightedGroup !== null && groupKeyFor(part) === highlightedGroup;
  };

  // While a part's menu is open, dim the rest of its own duplicate group —
  // editing this part will affect those too, so we keep them visible but de-emphasized
  const isPartDimmed = (part) => {
    if (selectedPartId === null || part.id === selectedPartId) return false;
    const selectedPart = parts.find((p) => p.id === selectedPartId);
    return selectedPart != null && groupKeyFor(part) === groupKeyFor(selectedPart);
  };

  // Entering edit mode hides every row's controls, which unmounts the open Dropdown before
  // it can report its own close — leaving selectedPartId stuck and blocking future highlights.
  // Syncing it directly to editPartId sidesteps that race entirely. When edit ends, carry the
  // edited part's highlight over to a normal background-click group highlight (instead of just
  // dropping it), so the user can naturally click another part to move the highlight from there.
  useEffect(() => {
    if (!editPartId && selectedPartId !== null) {
      const editedPart = parts.find((p) => p.id === selectedPartId);
      setHighlightedGroup(editedPart ? groupKeyFor(editedPart) : null);
    }
    setSelectedPartId(editPartId || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPartId]);

  // Escape clears the background-click highlight, but only when no part menu is open
  // (in that case the open Dropdown handles Escape itself, closing the menu first)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Escape' || selectedPartId !== null) return;
      setHighlightedGroup(null);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedPartId]);

  const initializeSortable = useCallback(() => {
    if (songPartsRef) {
      destroySortable();
      // Overview hides duplicates/notes, so the visible order is a subset — drag-and-drop
      // would reorder against a partial list, so it's disabled while filtering.
      // Also disabled while a part is being edited, since reordering underneath the
      // open editor could change which part the in-progress edit ends up writing to.
      // And while parts are being picked for their intensity, so a pick can't turn into a drag
      if (!overview && !editPartId) createSortable(songPartsRef, onPartSort);
    }
  }, [songPartsRef, onPartSort, overview, editPartId]);

  useEffect(() => {
    initializeSortable();
  }, [initializeSortable, parts.length]);

  useEffect(() => {
    return () => {
      destroySortable();
    };
  }, []);

  // Rounded corners must follow the first/last *visible* row, not the DOM first/last child:
  // in overview mode the hidden rows stay mounted (collapsed to 0 height), so :first-child /
  // :last-child would land the radius on an invisible row. We compute the visible endpoints here.
  const visibleParts = parts.filter((part) => !(overview && isHiddenInOverview(part)));
  const firstVisibleId = visibleParts[0]?.id;
  const lastVisibleId = visibleParts[visibleParts.length - 1]?.id;

  const songPartsClasses = cx('SongParts', { overview, editing: !!editPartId });

  const partNumbers = computePartNumbers(parts);

  // Editing a note turns the list into a picker: every part that can carry the note gets an
  // Attach toggle instead of its options menu, and the note can be attached to as many as needed
  const editingPart = editPartId ? parts.find((p) => p.id === editPartId) : null;
  const attachingNote = editingPart && isNotePart(editingPart) ? editingPart : null;

  return (
    <>
      {parts.length !== 0 ? (
        <ul key={refreshKey} className={songPartsClasses} ref={(ref) => setSongPartsRef(ref)}>
          {parts.map((part, partIndex) => (
            <SongPart
              part={part}
              parts={parts}
              partIndex={partIndex}
              displayNumber={partNumbers[part.id]}
              onPartMove={onPartMove}
              onPartDelete={onPartDelete}
              onPartDuplicate={onPartDuplicate}
              onPartEdit={onPartEdit}
              onPartIntensity={onPartIntensity}
              editPartId={editPartId}
              isHighlighted={isPartHighlighted(part)}
              isDimmed={isPartDimmed(part)}
              isCollapsed={overview && isHiddenInOverview(part)}
              isFirstVisible={part.id === firstVisibleId}
              isLastVisible={part.id === lastVisibleId}
              flashType={part.id === flashingPartId ? flashingType : null}
              onBackgroundClick={() => handleBackgroundClick(part)}
              onMenuOpenChange={(open) => handleMenuOpenChange(part, open)}
              attachedNoteCount={isNotePart(part) ? 0 : getAttachedNotes(parts, part.id).length}
              attachedToCount={isNotePart(part) ? countAttachedToParts(part, parts) : 0}
              canAttach={!!attachingNote && part.id !== attachingNote.id && canAttachNotesTo(part)}
              isAttached={!!attachingNote && isAttachedToPart(attachingNote, part.id)}
              onToggleAttach={() => onToggleAttach?.(attachingNote?.id, part.id)}
              timeSignature={timeSignature}
              key={part.id}
            />
          ))}
        </ul>
      ) : (
        <NoContent message={t('songForm.noParts')} />
      )}
    </>
  );
}

export default SongParts;
